/**
 * Panel Host - Shadow DOM container for CSS isolation
 *
 * Creates an isolated container using Shadow DOM to prevent
 * CSS leakage between the host page and the panel.
 */

const HOST_ID = 'data-notebook-host';

export interface PanelHost {
  /** The shadow root for rendering content */
  shadowRoot: ShadowRoot;
  /** The host element in the DOM */
  hostElement: HTMLDivElement;
  /** Render HTML content into the shadow root */
  render(html: string): void;
  /** Inject CSS into the shadow root. Pass an id to support multiple style blocks. */
  injectStyles(css: string, id?: string): void;
  /** Remove the host from the DOM */
  destroy(): void;
}

/**
 * Create or retrieve the panel host with Shadow DOM
 */
export function createPanelHost(): PanelHost {
  // Check if host already exists
  let hostElement = document.getElementById(HOST_ID) as HTMLDivElement | null;

  if (!hostElement) {
    // Create new host element
    hostElement = document.createElement('div');
    hostElement.id = HOST_ID;

    // Style the host element to not interfere with page layout
    hostElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      z-index: 2147483647;
      pointer-events: none;
    `;

    document.body.appendChild(hostElement);
  }

  // Get or create shadow root
  let shadowRoot = hostElement.shadowRoot;
  if (!shadowRoot) {
    shadowRoot = hostElement.attachShadow({ mode: 'open' });
  }

  return {
    shadowRoot,
    hostElement,

    render(html: string): void {
      // Find or create content container
      let container = shadowRoot.getElementById('dn-content');
      if (!container) {
        container = document.createElement('div');
        container.id = 'dn-content';
        shadowRoot.appendChild(container);
      }
      container.innerHTML = html;
    },

    injectStyles(css: string, id: string = 'dn-styles'): void {
      // Check if styles already injected with this id
      let styleEl = shadowRoot.getElementById(id) as HTMLStyleElement | null;
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = id;
        shadowRoot.insertBefore(styleEl, shadowRoot.firstChild);
      }
      styleEl.textContent = css;
    },

    destroy(): void {
      hostElement?.remove();
    },
  };
}

/**
 * Get the existing panel host if it exists
 */
export function getPanelHost(): PanelHost | null {
  const hostElement = document.getElementById(HOST_ID) as HTMLDivElement | null;
  if (!hostElement || !hostElement.shadowRoot) {
    return null;
  }

  return {
    shadowRoot: hostElement.shadowRoot,
    hostElement,
    render(html: string): void {
      let container = hostElement.shadowRoot!.getElementById('dn-content');
      if (!container) {
        container = document.createElement('div');
        container.id = 'dn-content';
        hostElement.shadowRoot!.appendChild(container);
      }
      container.innerHTML = html;
    },
    injectStyles(css: string, id: string = 'dn-styles'): void {
      let styleEl = hostElement.shadowRoot!.getElementById(id) as HTMLStyleElement | null;
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = id;
        hostElement.shadowRoot!.insertBefore(styleEl, hostElement.shadowRoot!.firstChild);
      }
      styleEl.textContent = css;
    },
    destroy(): void {
      hostElement.remove();
    },
  };
}
